import React, { useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Save, Eye, ArrowLeft, Settings, Undo2, Redo2 } from 'lucide-react';
import { IconTextButton } from '../ButtonComponents';
import { LabelCategory } from '../../types/label';
import { useNavigate } from 'react-router-dom';

interface TopBarProps {
  templateName: string;
  templateCode: string;
  category: LabelCategory | null;
  setCategory: (val: LabelCategory) => void;
  widthMm: number;
  setWidthMm: (val: number) => void;
  heightMm: number;
  setHeightMm: (val: number) => void;
  onSave: () => void;
  onPreview: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

export const TopBar: React.FC<TopBarProps> = ({
  templateName,
  templateCode,
  category,
  setCategory,
  widthMm,
  setWidthMm,
  heightMm,
  setHeightMm,
  onSave,
  onPreview,
  onUndo,
  onRedo,
  canUndo,
  canRedo
}) => {
  const navigate = useNavigate();
  const [designType, setDesignType] = useState<'LABEL' | 'PAGE'>('LABEL');
  const [showMobileSettings, setShowMobileSettings] = useState(false);

  const categories = Object.values(LabelCategory).map(c => ({ label: c, value: c }));

  const isA4 = widthMm === 210 && heightMm === 297;
  const isA5 = widthMm === 148 && heightMm === 210;
  const isA6 = widthMm === 105 && heightMm === 148;
  const selectedPageSize = isA4 ? 'a4' : isA5 ? 'a5' : isA6 ? 'a6' : null;

  const pageSizes = [
    { label: 'Seçiniz', value: null },
    { label: 'A4 (210x297 mm)', value: 'a4' },
    { label: 'A5 (148x210 mm)', value: 'a5' },
    { label: 'A6 (105x148 mm)', value: 'a6' }
  ];

  const handleSizeChange = (val: string | null) => {
    if (val === 'a4') { setWidthMm(210); setHeightMm(297); }
    else if (val === 'a5') { setWidthMm(148); setHeightMm(210); }
    else if (val === 'a6') { setWidthMm(105); setHeightMm(148); }
  };

  const labelSizes = [
    { label: 'Özel (Manuel)', value: null },
    { label: '100x100 mm', value: '100x100' },
    { label: '100x150 mm', value: '100x150' },
    { label: '50x50 mm', value: '50x50' },
    { label: '80x80 mm', value: '80x80' }
  ];

  const selectedLabelSize = 
    widthMm === 100 && heightMm === 100 ? '100x100' :
    widthMm === 100 && heightMm === 150 ? '100x150' :
    widthMm === 50 && heightMm === 50 ? '50x50' :
    widthMm === 80 && heightMm === 80 ? '80x80' : null;

  const handleLabelSizeChange = (val: string | null) => {
    if (val === '100x100') { setWidthMm(100); setHeightMm(100); }
    else if (val === '100x150') { setWidthMm(100); setHeightMm(150); }
    else if (val === '50x50') { setWidthMm(50); setHeightMm(50); }
    else if (val === '80x80') { setWidthMm(80); setHeightMm(80); }
  };

  return (
    <div className="bg-card rounded-xl border border-borderColor shadow-sm flex flex-col lg:flex-row lg:items-center justify-between p-3 gap-4">
      {/* Top Row on Mobile, Left Side on Desktop */}
      <div className="flex items-center justify-between w-full lg:w-auto gap-2">
        <div className="flex items-center gap-2 sm:gap-4">
          <button onClick={() => navigate("/firm/labels")} className="p-2 bg-bgAlt hover:bg-borderColor rounded-full transition-colors group flex-shrink-0">
            <ArrowLeft className="w-5 h-5 text-text/70 group-hover:text-text" />
          </button>
          <div className="flex flex-col">
            <h1 className="text-base sm:text-lg font-bold text-text leading-tight truncate max-w-[140px] sm:max-w-[300px]">{templateName || "Yükleniyor..."}</h1>
            <p className="text-[10px] sm:text-xs text-text/50 mt-0.5">Kod: <span className="font-mono bg-bgAlt px-1 sm:px-1.5 py-0.5 rounded text-text/70">{templateCode}</span></p>
          </div>
        </div>

        {/* Action Buttons for Mobile/Tablet */}
        <div className="flex lg:hidden items-center gap-1.5 sm:gap-2">
          <button 
            onClick={() => setShowMobileSettings(!showMobileSettings)} 
            className={`flex items-center justify-center p-2 rounded-md border transition-colors ${showMobileSettings ? 'bg-primary/10 text-primary border-primary/20' : 'bg-bgAlt text-text border-borderColor hover:bg-borderColor'}`}
            title="Ayarlar"
          >
            <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <div className="w-px h-5 bg-borderColor mx-0.5"></div>
          <button onClick={onPreview} className="flex items-center justify-center p-2 sm:px-3 sm:py-2 gap-2 rounded-md bg-bgAlt text-text border border-borderColor hover:bg-borderColor transition-colors">
            <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline-block text-sm font-medium">Önizleme</span>
          </button>
          <button onClick={onSave} className="flex items-center justify-center p-2 sm:px-4 sm:py-2 gap-2 rounded-md bg-primary text-white hover:bg-primaryHover transition-colors shadow-sm">
            <Save className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline-block text-sm font-medium">Kaydet</span>
          </button>
        </div>
      </div>

      {/* Middle/Right: Controls */}
      <div className={`${showMobileSettings ? 'flex' : 'hidden'} lg:flex flex-wrap items-center gap-3 w-full lg:w-auto mt-2 lg:mt-0 pt-2 lg:pt-0 border-t lg:border-t-0 border-borderColor`}>
        <Dropdown
          value={category}
          options={categories}
          onChange={(e) => setCategory(e.value)}
          placeholder="Kategori"
          className="w-full sm:w-36 p-inputtext-sm"
          disabled={true}
        />

        {/* Custom Toggle for Etiket / Sayfa */}
        <div className="flex bg-bgAlt rounded-lg p-1 border border-borderColor relative w-full sm:w-44 flex-shrink-0">
          <div
            className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-card rounded-md shadow-[0_1px_3px_rgba(0,0,0,0.1)] border border-borderColor/50 transition-all duration-300 ease-out ${designType === 'LABEL' ? 'left-1' : 'left-[calc(50%+2px)]'}`}
          />
          <button
            onClick={() => setDesignType('LABEL')}
            className={`flex-1 relative z-10 py-1.5 text-xs font-semibold transition-colors ${designType === 'LABEL' ? 'text-primary' : 'text-text/60 hover:text-text'}`}
          >
            Etiket
          </button>
          <button
            onClick={() => setDesignType('PAGE')}
            className={`flex-1 relative z-10 py-1.5 text-xs font-semibold transition-colors ${designType === 'PAGE' ? 'text-primary' : 'text-text/60 hover:text-text'}`}
          >
            Sayfa
          </button>
        </div>

        {/* Dynamic Inputs based on type */}
        {designType === 'PAGE' ? (
          <Dropdown
            value={selectedPageSize}
            options={pageSizes}
            onChange={(e) => handleSizeChange(e.value)}
            placeholder="Sayfa Boyutu"
            className="w-full sm:w-40 p-inputtext-sm"
          />
        ) : (
          <div className="flex items-center gap-2">
            <Dropdown
              value={selectedLabelSize}
              options={labelSizes}
              onChange={(e) => handleLabelSizeChange(e.value)}
              placeholder="Standart Boyut"
              className="w-full sm:w-36 p-inputtext-sm"
            />
            {selectedLabelSize === null && (
              <div className="flex items-center gap-2 bg-bgAlt/50 p-1 rounded-lg border border-borderColor w-full sm:w-auto justify-between sm:justify-start">
                <div className="flex items-center gap-1.5 px-2">
                  <span className="text-xs font-medium text-text/60">G (mm):</span>
                  <InputNumber value={widthMm} onValueChange={(e) => setWidthMm(e.value || 100)} className="w-16 sm:w-14" inputClassName="w-full p-inputtext-sm text-center p-1" />
                </div>
                <div className="w-px h-4 bg-borderColor"></div>
                <div className="flex items-center gap-1.5 px-2">
                  <span className="text-xs font-medium text-text/60">Y (mm):</span>
                  <InputNumber value={heightMm} onValueChange={(e) => setHeightMm(e.value || 100)} className="w-16 sm:w-14" inputClassName="w-full p-inputtext-sm text-center p-1" />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="hidden lg:block w-px h-8 bg-borderColor mx-1"></div>

        {/* Action Buttons (Desktop) */}
        <div className="hidden lg:flex items-center gap-2 ml-auto mt-2 lg:mt-0 w-full sm:w-auto justify-end">
          {onUndo && (
            <button onClick={onUndo} disabled={!canUndo} className="p-2 bg-bgAlt text-text border border-borderColor hover:bg-borderColor rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <Undo2 className="w-5 h-5" />
            </button>
          )}
          {onRedo && (
            <button onClick={onRedo} disabled={!canRedo} className="p-2 bg-bgAlt text-text border border-borderColor hover:bg-borderColor rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <Redo2 className="w-5 h-5" />
            </button>
          )}
          <IconTextButton onClick={onPreview} icon={Eye} className="p-button-secondary p-button-outlined bg-bgAlt text-text border-borderColor hover:bg-borderColor px-3 py-2 ml-2">
            <span className="text-sm font-medium">Önizleme</span>
          </IconTextButton>
          <IconTextButton onClick={onSave} icon={Save} className="bg-primary text-white hover:bg-primaryHover px-4 py-2 shadow-sm">
            <span className="text-sm font-medium">Kaydet</span>
          </IconTextButton>
        </div>
      </div>
    </div>
  );
};
